import { api } from '@xtnd/web/shared/api';

/**
 * Public credential metadata returned by the V2 Clio credentials API.
 * Mirrors @repo/shared's PublicCredentialMetadata — duplicated here to avoid
 * pulling the shared package (with its AWS SDK deps) into the web bundle.
 */
export interface PublicCredentialMetadata {
  credentialId: string;
  label: string;
  role: 'primary' | 'secondary';
  appId: string;
  clientIdPrefix: string;
  enabled: boolean;
  hasAccessToken: boolean;
  hasRefreshToken: boolean;
  tokenExpiresAt: string | null;
  lastRefreshedAt: string | null;
}

export interface PublicCredentialBundle {
  configured: boolean;
  primaryCredentialId: string | null;
  credentialCount: number;
  credentials: PublicCredentialMetadata[];
}

export interface RegisterCredentialRequest {
  label: string;
  clientId: string;
  clientSecret: string;
  appId?: string;
  redirectUri: string;
}

export interface RegisterCredentialResponse {
  credentialId: string;
  oauthAuthorizeUrl: string;
  state: string;
}

export interface UpdateCredentialRequest {
  label?: string;
  enabled?: boolean;
}

export interface ReauthorizeRequest {
  redirectUri: string;
}

export interface ReauthorizeResponse {
  oauthAuthorizeUrl: string;
  state: string;
}

class ClioCredentialsApi {
  private readonly base = '/tenants/current/clio/credentials';

  list(): Promise<PublicCredentialBundle> {
    return api.get<PublicCredentialBundle>(this.base);
  }

  register(request: RegisterCredentialRequest): Promise<RegisterCredentialResponse> {
    return api.post<RegisterCredentialResponse>(this.base, request);
  }

  update(
    credentialId: string,
    patch: UpdateCredentialRequest,
  ): Promise<PublicCredentialMetadata> {
    return api.patch<PublicCredentialMetadata>(`${this.base}/${credentialId}`, patch);
  }

  remove(credentialId: string): Promise<void> {
    return api.delete<void>(`${this.base}/${credentialId}`);
  }

  reauthorize(
    credentialId: string,
    request: ReauthorizeRequest,
  ): Promise<ReauthorizeResponse> {
    return api.post<ReauthorizeResponse>(
      `${this.base}/${credentialId}/reauthorize`,
      request,
    );
  }
}

export const clioCredentialsApi = new ClioCredentialsApi();
